/**
 * Landing-page section / card model.
 *
 * Each section renders as a banded header + grid of cards. Each card has a
 * primary destination plus optional `links` shown as inline chips so players
 * can jump straight to a sub-page (mount list, EHP calculator, etc.) instead
 * of bouncing through the section landing.
 */

export interface HomeCardLink {
  label: string;
  routerLink: string;
  /** Query params appended to the routerLink (e.g. { tab: 'combat' }). */
  queryParams?: Record<string, string>;
  /** If true, the chip is hidden unless labs mode is on. */
  labsOnly?: boolean;
}

export interface HomeCard {
  label: string;
  desc: string;
  icon: string;
  routerLink: string;
  /** Sub-page quick links. Shown as chips below the description. */
  links?: HomeCardLink[];
  /** Optional accent class — drives the card's hover-glow color. */
  accent?: 'build' | 'combat' | 'gear' | 'mounts' | 'reference' | 'community' | 'guild' | 'legacy';
  /** If true, the entire card is hidden unless labs mode is on. */
  labsOnly?: boolean;
  /** If true, render with a slightly larger "featured" treatment. */
  featured?: boolean;
}

export interface HomeSection {
  /** Anchor / id-safe key. */
  key: string;
  title: string;
  /** One-sentence positioning blurb under the heading. */
  blurb?: string;
  /** Material icon shown beside the section title. */
  icon: string;
  cards: HomeCard[];
}

export const HOME_SECTIONS: HomeSection[] = [
  {
    key: 'community',
    title: 'Community',
    blurb: 'Find people, guilds, and the latest game news.',
    icon: 'forum',
    cards: [
      {
        label: 'Guilds & Alliances',
        desc: 'Filter by platform, language, time of day',
        icon: 'flag',
        routerLink: '/guilds',
        accent: 'community',
      },
      {
        label: 'Game News',
        desc: 'Latest Neverwinter news & updates',
        icon: 'newspaper',
        routerLink: '/news',
        accent: 'community',
      },
      {
        label: 'Discord Servers',
        desc: 'Community Discords by class, role, region',
        icon: 'forum',
        routerLink: '/discords',
        accent: 'community',
      },
      {
        label: 'Community Feedback',
        desc: 'AI-summarized Discord discussion topics',
        icon: 'auto_stories',
        routerLink: '/discord-summaries',
        accent: 'community',
        labsOnly: true,
      },
    ],
  },
  {
    key: 'build',
    title: 'Build & Sim Tools',
    blurb: 'Plan a character, compare DPS / EHP, and validate rotations.',
    icon: 'build',
    cards: [
      {
        label: "Rainer's Tools",
        desc: 'Build planner & stat optimizer',
        icon: 'tune',
        routerLink: '/rainer',
        accent: 'build',
        featured: true,
        links: [
          { label: 'Public Builds', routerLink: '/rainer/builds' },
          { label: 'Contests', routerLink: '/rainer/contests' },
        ],
      },
      {
        label: 'Character Editor',
        desc: 'Build your character & save it to the cloud',
        icon: 'manage_accounts',
        routerLink: '/characters',
        accent: 'build',
        featured: true,
        links: [
          { label: 'Shared Builds', routerLink: '/shares' },
        ],
      },
      {
        label: 'Rotation Simulator',
        desc: 'Tick-by-tick rotation DPS simulation',
        icon: 'speed',
        routerLink: '/sim',
        accent: 'build',
        labsOnly: true,
      },
      {
        label: 'Combat Log Parser',
        desc: 'Drop a log, get a damage breakdown',
        icon: 'analytics',
        routerLink: '/logparser',
        accent: 'build',
        links: [
          { label: 'How to enable logs', routerLink: '/logparser/how-to' },
        ],
      },
    ],
  },
  {
    key: 'combat',
    title: 'Combat Mechanics',
    blurb: 'Formulas, calculators, and rating math.',
    icon: 'auto_fix_high',
    cards: [
      {
        label: 'Mechanics Overview',
        desc: 'Damage / Healing / EHP / stats — explained',
        icon: 'menu_book',
        routerLink: '/mechanics',
        accent: 'combat',
        links: [
          { label: 'General Intro', routerLink: '/mechanics/general' },
          { label: 'Stats', routerLink: '/mechanics/stats' },
          { label: 'Power', routerLink: '/mechanics/power' },
          { label: 'Recharge Speed', routerLink: '/mechanics/recovery' },
        ],
      },
      {
        label: 'Damage Formula',
        desc: 'How damage actually rolls up',
        icon: 'whatshot',
        routerLink: '/mechanics/damagemath',
        accent: 'combat',
        links: [{ label: 'Damage Calculator', routerLink: '/mechanics/damagecalc' }],
      },
      {
        label: 'Healing Formula',
        desc: 'Outgoing & incoming heal math',
        icon: 'healing',
        routerLink: '/mechanics/healingmath',
        accent: 'combat',
        links: [{ label: 'Healing Calculator', routerLink: '/mechanics/healingcalc' }],
      },
      {
        label: 'Effective HP',
        desc: 'How DR, deflect, HP & buffs combine',
        icon: 'shield',
        routerLink: '/mechanics/ehpmath',
        accent: 'combat',
        links: [{ label: 'EHP Calculator', routerLink: '/mechanics/ehpcalc' }],
      },
      {
        label: 'Rating Calculator',
        desc: 'Convert ratings ↔ % at any item level',
        icon: 'calculate',
        routerLink: '/mechanics/statcalc',
        accent: 'combat',
      },
    ],
  },
  {
    key: 'gear-equipment',
    title: 'Gear & Equipment',
    blurb: 'Gear, companions, mounts, and the math that goes with them.',
    icon: 'shield',
    cards: [
      {
        label: 'Gear',
        desc: 'Top-tier gear with stats & set bonuses',
        icon: 'shield',
        routerLink: '/gear',
        accent: 'gear',
        links: [
          { label: 'Gear List', routerLink: '/gear/list' },
          { label: 'Companion Gear', routerLink: '/gear/companion' },
        ],
      },
      {
        label: 'Enchantments',
        desc: 'Combat & stat enchantments',
        icon: 'auto_awesome',
        routerLink: '/enchantments/list',
        accent: 'gear',
        links: [
          { label: 'Standard', routerLink: '/enchantments/list', queryParams: { tab: 'standard' } },
          { label: 'Combat', routerLink: '/enchantments/list', queryParams: { tab: 'combat' } },
          { label: 'Overload', routerLink: '/enchantments/list', queryParams: { tab: 'overload' } },
          { label: 'Bonus', routerLink: '/enchantments/list', queryParams: { tab: 'bonus' } },
          { label: 'Companion', routerLink: '/enchantments/list', queryParams: { tab: 'companion' } },
        ],
      },
      {
        label: 'Artifacts',
        desc: 'Artifact equipment & powers',
        icon: 'diamond',
        routerLink: '/artifacts/list',
        accent: 'gear',
      },
      {
        label: 'Consumables',
        desc: 'Elixirs, potions & buff food',
        icon: 'local_pharmacy',
        routerLink: '/consumables',
        accent: 'gear',
      },
      {
        label: 'Companions',
        desc: 'Companion database & stable simulator',
        icon: 'groups',
        routerLink: '/companions',
        accent: 'mounts',
        links: [
          { label: 'Companion List', routerLink: '/companions/list' },
          { label: 'Player Bonuses', routerLink: '/companions/powers' },
          { label: 'Enhancement Powers', routerLink: '/companions/enhancements' },
          { label: 'Stable Sim', routerLink: '/companions/stable' },
        ],
      },
      {
        label: 'Mounts & Insignias',
        desc: 'Mount database, insignias & stable tools',
        icon: 'pets',
        routerLink: '/mounts',
        accent: 'mounts',
        links: [
          { label: 'Mount List', routerLink: '/mounts/list' },
          { label: 'Insignia Bonuses', routerLink: '/mounts/bonuses' },
          { label: 'Insignia Items', routerLink: '/mounts/insignias' },
          { label: 'Collars', routerLink: '/mounts/collars' },
          { label: 'Stable Sim', routerLink: '/mounts/stable' },
        ],
      },
      {
        label: 'Upgrade Cost Calculator',
        desc: 'Enchantment & artifact upgrade AD cost — motes, wards, tiers',
        icon: 'calculate',
        routerLink: '/upgrade-cost',
        accent: 'gear',
        links: [
          { label: 'Enchantments', routerLink: '/upgrade-cost', queryParams: { tab: 'enchant' } },
          { label: 'Artifacts', routerLink: '/upgrade-cost', queryParams: { tab: 'artifact' } },
          { label: 'Insignias', routerLink: '/upgrade-cost', queryParams: { tab: 'insignia' } },
          { label: 'Companions', routerLink: '/upgrade-cost', queryParams: { tab: 'companion' } },
        ],
      },
    ],
  },
  {
    key: 'packs',
    title: 'Packs & Lockboxes',
    blurb: 'Reward contents and drop rates.',
    icon: 'card_giftcard',
    cards: [
      {
        label: 'Packs & Lockboxes',
        desc: 'Lockboxes & reward packs by date',
        icon: 'card_giftcard',
        routerLink: '/packs',
        accent: 'gear',
      },
    ],
  },
  {
    key: 'reference',
    title: 'Game Reference',
    blurb: 'Classes, races & quick-reference info.',
    icon: 'school',
    cards: [
      {
        label: 'Classes & Races',
        desc: 'Class powers, feats, features & racial traits',
        icon: 'school',
        routerLink: '/classes',
        accent: 'reference',
        links: [
          { label: 'All Classes', routerLink: '/classes' },
          { label: 'Races', routerLink: '/classes/races' },
        ],
      },
      {
        label: 'Other Info',
        desc: 'Aliases, binds & quick-reference',
        icon: 'info',
        routerLink: '/other-info',
        accent: 'reference',
        links: [
          { label: 'Aliases & Binds', routerLink: '/other-info/aliases-and-binds' },
          { label: 'Alias/Bind File Builder', routerLink: '/other-info/file-builder' },
        ],
      },
      {
        label: 'External Tools & Info',
        desc: 'External tools & miscellaneous links',
        icon: 'open_in_new',
        routerLink: '/other-info/tools',
        accent: 'reference',
        links: [
          { label: 'Tools', routerLink: '/other-info/tools' },
          { label: 'Misc Links', routerLink: '/other-info/misc' },
        ],
      },
    ],
  },
  {
    key: 'guild-tools',
    title: 'Guild Tools',
    blurb: 'Stronghold costs, guild log & roster.',
    icon: 'castle',
    cards: [
      {
        label: 'Stronghold Costs',
        desc: 'Materials needed to upgrade structures',
        icon: 'foundation',
        routerLink: '/guild-tools/costs',
        accent: 'guild',
      },
      {
        label: 'Guild Log',
        desc: 'Parse & analyze guild contribution logs',
        icon: 'description',
        routerLink: '/guild-tools/log',
        accent: 'guild',
      },
      {
        label: 'Guild Roster',
        desc: 'Compare members & track activity',
        icon: 'group',
        routerLink: '/guild-tools/roster',
        accent: 'guild',
      },
    ],
  },
  {
    key: 'legacy',
    title: 'Legacy & Archive',
    blurb: 'Older write-ups kept around for reference.',
    icon: 'archive',
    cards: [
      {
        label: 'Old JanneNW Site',
        desc: 'Historical mod 6-19 mechanics, calculators & class resource pages',
        icon: 'auto_stories',
        routerLink: '/old-janne/about',
        accent: 'legacy',
        links: [
          { label: 'Overview', routerLink: '/old-janne/basics/overview' },
          { label: 'M16 Formulas', routerLink: '/old-janne/mechanics16/formulas' },
          { label: 'M19 Stat Calc', routerLink: '/old-janne/mechanics19/stat-calc' },
          { label: 'Class E-Resources', routerLink: '/old-janne/e-resources' },
        ],
      },
    ],
  },
];
